/**
 * Main application JavaScript
 * Handles theme functionality and interactions
 */

class ThemeApp {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupProductVariants();
    this.setupQuantitySelectors();
    this.setupCart();
    this.setupAjaxForms();
    this.setupLazyLoading();
    this.setupAnimations();
  }

  // Mobile menu functionality
  setupMobileMenu() {
    const mobileMenuButton = document.getElementById('mobile-menu-button');
    const mobileMenu = document.getElementById('mobile-menu');
    
    if (mobileMenuButton && mobileMenu) {
      mobileMenuButton.addEventListener('click', () => {
        const isExpanded = mobileMenuButton.getAttribute('aria-expanded') === 'true';
        mobileMenuButton.setAttribute('aria-expanded', !isExpanded);
        mobileMenu.hidden = isExpanded;
        
        // Toggle body scroll
        document.body.classList.toggle('overflow-hidden', !isExpanded);
      });
    }

    // Mobile dropdown toggles
    const mobileDropdowns = document.querySelectorAll('[data-mobile-dropdown-trigger]');
    mobileDropdowns.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const content = trigger.parentElement.querySelector('.mobile-dropdown-content');
        const icon = trigger.querySelector('svg');
        
        if (content && icon) {
          const isHidden = content.classList.contains('hidden');
          content.classList.toggle('hidden', !isHidden);
          icon.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)';
        }
      });
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', (e) => {
      if (mobileMenu && !mobileMenu.contains(e.target) && !mobileMenuButton.contains(e.target)) {
        mobileMenuButton.setAttribute('aria-expanded', 'false');
        mobileMenu.hidden = true;
        document.body.classList.remove('overflow-hidden');
      }
    });
  }

  // Product variant functionality (simplified for standard Shopify behavior)
  setupProductVariants() {
    // Variant functionality is now handled by standard Shopify selectors
    // No custom JavaScript needed - Shopify handles variant selection natively
    console.log('Using standard Shopify variant selection');
  }

  handleVariantChange(event) {
    // This method is no longer needed as Shopify handles variant changes natively
    // Keeping for backward compatibility but functionality moved to standard selectors
  }

  updateProductInfo(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Update price
    const newPrice = doc.querySelector('#ProductPrice');
    const currentPrice = document.querySelector('#ProductPrice');
    if (newPrice && currentPrice) {
      currentPrice.textContent = newPrice.textContent;
    }

    // Update compare at price
    const newComparePrice = doc.querySelector('#ProductCompareAtPrice');
    const currentComparePrice = document.querySelector('#ProductCompareAtPrice');
    if (newComparePrice && currentComparePrice) {
      currentComparePrice.textContent = newComparePrice.textContent;
    } else if (currentComparePrice && !newComparePrice) {
      currentComparePrice.style.display = 'none';
    }

    // Update add to cart button
    const newButton = doc.querySelector('.add-to-cart-text');
    const currentButton = document.querySelector('.add-to-cart-text');
    if (newButton && currentButton) {
      currentButton.textContent = newButton.textContent;
    }
  }

  // Quantity selector functionality
  setupQuantitySelectors() {
    // Remove existing listeners to prevent duplicates
    const existingButtons = document.querySelectorAll('.asby-qty-btn[data-listener="true"]');
    existingButtons.forEach(btn => btn.removeEventListener('click', this.handleQuantityChange));
    
    const quantityButtons = document.querySelectorAll('.asby-qty-btn');
    
    quantityButtons.forEach(button => {
      if (!button.dataset.listener) {
        button.addEventListener('click', this.handleQuantityChange.bind(this));
        button.dataset.listener = 'true';
      }
    });
  }

  handleQuantityChange(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const change = button.dataset.quantity;
    const line = button.dataset.line;
    
    let input;
    if (line) {
      input = document.querySelector(`input[data-line="${line}"]`);
    } else {
      input = button.parentElement.querySelector('.asby-qty-input');
    }
    
    if (!input) return;
    
    let currentValue = parseInt(input.value) || 1;
    
    if (change === '+1') {
      input.value = currentValue + 1;
    } else if (change === '-1' && currentValue > 1) {
      input.value = currentValue - 1;
    }
    
    // Trigger change event
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Cart functionality
  setupCart() {
    // Update cart count
    this.updateCartCount();
    
    // Handle cart form submissions
    const cartForms = document.querySelectorAll('.cart-form');
    cartForms.forEach(form => {
      form.addEventListener('submit', this.handleCartFormSubmit.bind(this));
    });

    // Auto-update cart on quantity change
    const cartQuantityInputs = document.querySelectorAll('.cart-form input[name="updates[]"]');
    cartQuantityInputs.forEach(input => {
      let timeout;
      input.addEventListener('change', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          this.updateCart();
        }, 500);
      });
    });
  }

  handleCartFormSubmit(event) {
    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    
    if (submitButton) {
      const originalText = submitButton.textContent;
      submitButton.textContent = 'Adding...';
      submitButton.disabled = true;
      
      // Re-enable after a delay if not redirecting
      setTimeout(() => {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
      }, 3000);
    }
  }

  updateCart() {
    const cartForm = document.querySelector('.cart-form');
    if (!cartForm) return;

    const formData = new FormData(cartForm);
    
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new URLSearchParams(formData)
    })
    .then(response => response.json())
    .then(cart => {
      this.updateCartCount(cart.item_count);
      // Optionally reload the page or update cart content
      window.location.reload();
    })
    .catch(error => {
      console.error('Error updating cart:', error);
    });
  }

  updateCartCount(count) {
    if (typeof count === 'undefined') {
      // Fetch current cart count
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.updateCartCountDisplay(cart.item_count);
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
        });
    } else {
      this.updateCartCountDisplay(count);
    }
  }

  updateCartCountDisplay(count) {
    const cartCountElements = document.querySelectorAll('.cart-count-bubble');
    cartCountElements.forEach(element => {
      if (count > 0) {
        element.textContent = count;
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    });
  }

  // AJAX form handling
  setupAjaxForms() {
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    forms.forEach(form => {
      form.addEventListener('submit', this.handleAjaxFormSubmit.bind(this));
    });
  }

  handleAjaxFormSubmit(event) {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    const submitButton = form.querySelector('button[type="submit"]');
    const errorContainer = form.querySelector('.product-form__errors');
    
    // Show loading state
    if (submitButton) {
      submitButton.disabled = true;
      const spinner = submitButton.querySelector('.loading-overlay__spinner');
      const text = submitButton.querySelector('.add-to-cart-text');
      
      if (spinner && text) {
        spinner.classList.remove('hidden');
        text.style.opacity = '0';
      }
    }

    // Hide previous errors
    if (errorContainer) {
      errorContainer.hidden = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(item => {
      // Success - show notification or redirect
      this.showCartNotification(item);
      this.updateCartCount();
    })
    .catch(error => {
      // Show error
      console.error('Error adding to cart:', error);
      if (errorContainer) {
        errorContainer.hidden = false;
      }
    })
    .finally(() => {
      // Reset button state
      if (submitButton) {
        submitButton.disabled = false;
        const spinner = submitButton.querySelector('.loading-overlay__spinner');
        const text = submitButton.querySelector('.add-to-cart-text');
        
        if (spinner && text) {
          spinner.classList.add('hidden');
          text.style.opacity = '1';
        }
      }
    });
  }

  showCartNotification(item) {
    // Simple notification - can be enhanced with a modal or slide-in
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50 animate-fade-in';
    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        <span>Added to cart!</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  // Lazy loading setup
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;
            
            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
            }
            
            observer.unobserve(img);
          }
        });
      });

      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  // Animation setup
  setupAnimations() {
    if ('IntersectionObserver' in window) {
      const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        rootMargin: '0px 0px -100px 0px'
      });

      const animatedElements = document.querySelectorAll('.animate-slide-up, .animate-fade-in');
      animatedElements.forEach(element => {
        animationObserver.observe(element);
      });
    }
  }
}

// Initialize the app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ThemeApp();
});

// Handle theme editor events
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ThemeApp();
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    new ThemeApp();
  });
}

// Export for external use
window.ThemeApp = ThemeApp;